import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type TaskResultJudgement = "合格" | "条件付合格" | "不合格";

export type DailyResult =
  | "良好"
  | "要確認"
  | "要対応"
  | "異常あり"
  | "その他";

export interface ExecutionAttachment {
  attachment_id: string;
  file_name: string;
  s3_key: string;
  uploaded_at: string;
}

export interface CustomFieldValue {
  field_id: string;
  value: string | number | boolean | string[] | null;
}

export interface ExecutionRecord {
  execution_id: string;
  date: string;
  time?: string;
  checker?: string;
  result?: DailyResult;
  findings?: string;
  attachments?: ExecutionAttachment[];
  note?: string;
  finding_status?: string;
  finding_action?: string;
  finding_resolved_date?: string;
}

export interface ManagementTaskResultRecord {
  client_id: string;
  sort_key: string;
  result_id: string;
  property_id: string;
  task_id: string;

  year: number;
  month: number;
  year_month: string;

  planned: boolean;
  planned_dates?: number[];
  completed: boolean;

  execution_records: ExecutionRecord[];

  total_judgement?: TaskResultJudgement;
  remarks?: string;
  other_attachments?: ExecutionAttachment[];

  /**
   * planned_is_legal / actual_is_legal は管理業務マスタ
   * (ManagementTask.is_legal_inspection) に統合済み。
   * 旧データには値が残るが UI からは参照しない。
   */
  planned_is_legal?: boolean;
  /** 点検タスクのその月の「実施予定」がリモート点検かどうか */
  planned_is_remote?: boolean;
  actual_is_legal?: boolean;
  /** 点検タスクのその月の「実績」がリモート点検として実施されたか */
  actual_is_remote?: boolean;

  custom_fields?: CustomFieldValue[];

  created_at: string;
  updated_at?: string;
}

export interface UpsertTaskResultBody {
  property_id: string;
  task_id: string;
  year: number;
  month: number;
  planned?: boolean;
  planned_dates?: number[];
  completed?: boolean;
  execution_records?: ExecutionRecord[];
  total_judgement?: TaskResultJudgement;
  remarks?: string;
  other_attachments?: ExecutionAttachment[];
  // 法定フラグは ManagementTask.is_legal_inspection に統合済みのため送らない。
  planned_is_remote?: boolean;
  actual_is_remote?: boolean;
  custom_fields?: CustomFieldValue[];
}

export interface BulkUpsertTaskResultsBody {
  property_id: string;
  year: number;
  results: Array<Omit<UpsertTaskResultBody, "property_id" | "year">>;
}

interface ManagementTaskResultState {
  list: {
    data: ManagementTaskResultRecord[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
  };
}

const initialState: ManagementTaskResultState = {
  list: { data: [], loading: false, error: null, loaded: false },
};

export const fetchTaskResults = createAsyncThunk<
  ManagementTaskResultRecord[],
  { property_id: string; year: number; month?: number; task_id?: string }
>("managementTaskResults/fetch", async (params, { rejectWithValue }) => {
  try {
    const queryParams: Record<string, string> = {
      property_id: params.property_id,
      year: String(params.year),
    };
    if (params.month !== undefined) queryParams.month = String(params.month);
    if (params.task_id) queryParams.task_id = params.task_id;
    const res = await ipponApi.get(
      `${BaseURL}/api/v1/management-task-results`,
      { params: queryParams },
    );
    return res.data?.data?.items || [];
  } catch {
    return rejectWithValue("業務実績の取得に失敗しました");
  }
});

export const upsertTaskResult = createAsyncThunk<
  ManagementTaskResultRecord,
  UpsertTaskResultBody
>("managementTaskResults/upsert", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.put(
      `${BaseURL}/api/v1/management-task-results`,
      body,
    );
    return res.data.data;
  } catch {
    return rejectWithValue("業務実績の登録に失敗しました");
  }
});

export const bulkUpsertTaskResults = createAsyncThunk<
  ManagementTaskResultRecord[],
  BulkUpsertTaskResultsBody
>("managementTaskResults/bulkUpsert", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/management-task-results/bulk`,
      body,
    );
    return res.data?.data?.items || [];
  } catch {
    return rejectWithValue("業務実績の一括登録に失敗しました");
  }
});

export const deleteTaskResult = createAsyncThunk<
  { task_id: string; year: string; month: string },
  { task_id: string; year: string; month: string }
>("managementTaskResults/delete", async (params, { rejectWithValue }) => {
  try {
    await ipponApi.delete(
      `${BaseURL}/api/v1/management-task-results/${params.task_id}/${params.year}/${params.month}`,
    );
    return params;
  } catch {
    return rejectWithValue("業務実績の削除に失敗しました");
  }
});

const managementTaskResultSlice = createSlice({
  name: "managementTaskResults",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskResults.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchTaskResults.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
        state.list.loaded = true;
      })
      .addCase(fetchTaskResults.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = (action.payload as string) || null;
      })
      .addCase(upsertTaskResult.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (r) => r.sort_key === action.payload.sort_key,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
        else state.list.data.push(action.payload);
      })
      .addCase(bulkUpsertTaskResults.fulfilled, (state, action) => {
        for (const rec of action.payload) {
          const idx = state.list.data.findIndex(
            (r) => r.sort_key === rec.sort_key,
          );
          if (idx >= 0) state.list.data[idx] = rec;
          else state.list.data.push(rec);
        }
      })
      .addCase(deleteTaskResult.fulfilled, (state, action) => {
        const monthNum = Number(action.payload.month);
        const yearNum = Number(action.payload.year);
        state.list.data = state.list.data.filter(
          (r) =>
            !(
              r.task_id === action.payload.task_id &&
              r.year === yearNum &&
              r.month === monthNum
            ),
        );
      });
  },
});

export default managementTaskResultSlice.reducer;
